import { useState } from "react";
import { useRecordContext, usePermissions, type RaRecord } from "react-admin";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { formatBookingSadaratFromEventLine } from "@/utils/resolve-booking-identity-display";
import { buildBookingShowUrl } from "@/utils/route-utility";
import { hasPermission } from "@/utils/permission-utils";
import RazaRequestCreateBookingDialog from "./RazaRequestCreateBookingDialog";

type BookingSummary = {
  id: string;
  bookingNo: string | null;
  sadarat: string | null;
  hallBookingCount: number;
};

type RazaRequestRecord = RaRecord & {
  booking?: BookingSummary | null;
  name?: unknown;
  itsNo?: unknown;
};

/** Shows linked hall booking (one per raza) with counts and a shortcut to the booking record. */
const RazaRequestBookingSummary = () => {
  const record = useRecordContext<RazaRequestRecord>();
  const booking = record?.booking;
  const razaRequestId = record?.id != null ? String(record.id) : "";
  const { permissions } = usePermissions();
  const canCreateBooking = hasPermission(permissions, "bookings.create");
  const [createOpen, setCreateOpen] = useState(false);

  const defaultOrganiser = typeof record?.name === "string" ? record.name : "";
  const defaultItsNo = typeof record?.itsNo === "string" ? record.itsNo : "";

  if (!booking?.id) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          No hall booking exists for this raza yet. Create a booking here with this raza request
          pre-filled — you can add hall slots afterward without leaving this page.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          {canCreateBooking && razaRequestId ? (
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              onClick={() => setCreateOpen(true)}
            >
              Create hall booking
            </Button>
          ) : null}
        </Box>
        {createOpen && canCreateBooking && razaRequestId ? (
          <RazaRequestCreateBookingDialog
            onClose={() => setCreateOpen(false)}
            razaRequestId={razaRequestId}
            defaultOrganiser={defaultOrganiser}
            defaultItsNo={defaultItsNo}
          />
        ) : null}
      </Box>
    );
  }

  const sadaratLine = formatBookingSadaratFromEventLine(booking.sadarat);

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.default" }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Booking
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {booking.bookingNo ?? booking.id}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        Hall slots: {booking.hallBookingCount}
      </Typography>
      {sadaratLine ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {sadaratLine}
        </Typography>
      ) : null}
      <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          endIcon={<OpenInNewIcon sx={{ fontSize: "1rem" }} />}
          onClick={() => {
            window.open(buildBookingShowUrl(booking.id), "_blank", "noopener,noreferrer");
          }}
        >
          Open booking
        </Button>
      </Box>
    </Paper>
  );
};

export default RazaRequestBookingSummary;
